import { buildProductJsonLd, siteMetadata } from './site-metadata';

const PRODUCT_ARTIFACTS = [
  'NCR-Reorder-Control-Workbook.xlsx',
  'NCR-Proof-Checklist.pdf',
  'NCR-Reorder-Trigger-Sheet.pdf',
  'NCR-Vendor-Quote-Form.pdf',
  'Pad-Labels.pdf',
  'Carton-Labels.pdf',
  'form-id-ledger-template.csv',
  'number-range-template.csv',
  'label-data-template.csv',
];

const WORKBOOK_SHEETS = [
  'Form ID Ledger',
  'Number Range Tracker',
  'Reorder Trigger Sheet',
  'Vendor Quote Form',
  'Label Data',
];

export function getCheckoutHref(checkoutUrl?: string): string {
  return checkoutUrl?.trim() || '#inside-title';
}

export function buildHomePageMarkup(checkoutUrl?: string): string {
  const checkoutHref = getCheckoutHref(checkoutUrl);
  const checkoutLabel = checkoutHref.startsWith('#') ? 'View what is inside' : 'Buy the pack';
  const checkoutUrlForSchema = checkoutHref.startsWith('#') ? undefined : checkoutHref;

  return `
    <script type="application/ld+json">${buildProductJsonLd(checkoutUrlForSchema)}</script>
    <section class="hero" aria-labelledby="page-title">
      <div class="hero__copy">
        <p class="eyebrow">Sequential NCR control system</p>
        <h1 id="page-title">NCR Reorder Control Pack</h1>
        <p class="lede">
          Stop guessing the next carbonless form number. This downloadable workbook and
          printable proof pack is for print shops, copy centers, brokers, and operators
          who repeat numbered NCR jobs.
        </p>
        <div class="hero__actions">
          <a class="button button--primary" href="${checkoutHref}">${checkoutLabel}</a>
          <a class="button button--secondary" href="./Free-NCR-Proof-Checklist.pdf">Free NCR Proof Checklist</a>
        </div>
      </div>
      <figure class="product-preview">
        <img
          src="${siteMetadata.previewImagePath}"
          alt="Preview of the NCR Reorder Control Pack workbook, range tracker, and labels"
          width="1200"
          height="630"
        />
      </figure>
    </section>

    <section class="strip" aria-label="Product summary">
      <div><strong>$99</strong><span>One-time digital download.</span></div>
      <div><strong>XLSX workbook</strong><span>Ledger, range tracker, reorder trigger, quote form, and label data.</span></div>
      <div><strong>Proof PDFs</strong><span>Checklist, trigger sheet, quote form, pad labels, and carton labels.</span></div>
      <div><strong>CSV templates</strong><span>Form ID, number range, and label data templates.</span></div>
    </section>

    <section class="section section--grid" aria-labelledby="inside-title">
      <div>
        <p class="eyebrow">What is inside</p>
        <h2 id="inside-title">Workbook, labels, PDFs, and CSV templates.</h2>
        <p>
          The pack is built around repeat orders: identify the form, record the prior range,
          calculate the next start, prepare the proof, label the pads, and request the vendor quote.
        </p>
      </div>
      <div class="artifact-list">
        ${PRODUCT_ARTIFACTS.map((artifact) => `<span>${artifact}</span>`).join('')}
      </div>
    </section>

    <section class="section sheet-section" aria-labelledby="workbook-title">
      <div>
        <p class="eyebrow">Workbook sheets</p>
        <h2 id="workbook-title">Built for range control, not bookkeeping advice.</h2>
      </div>
      <ol class="sheet-list">
        ${WORKBOOK_SHEETS.map((sheet) => `<li>${sheet}</li>`).join('')}
      </ol>
    </section>

    <section class="section boundary" aria-labelledby="support-title">
      <p class="eyebrow">Support scope</p>
      <h2 id="support-title">Support is limited by design.</h2>
      <p>
        Supported: download access, print layout, and broken formulas.
      </p>
      <p>
        No tax, receipt-law, payment, imposition, or numbering setup advice. No press settings,
        paper chemistry, record-retention guidance, custom imports, or vendor negotiation.
      </p>
    </section>

    <section class="section checkout-note" aria-labelledby="delivery-title">
      <p class="eyebrow">Delivery model</p>
      <h2 id="delivery-title">Hosted digital delivery after purchase.</h2>
      <p>
        The $99 ZIP is a one-time digital download. After purchase, the checkout provider
        delivers the file and receipt. This site explains the contents, support boundary,
        and free checklist before purchase.
      </p>
    </section>
  `;
}
